import React, { useCallback, useEffect, useState } from 'react';

import {
  fetchOwnershipFlow,
  type OwnershipFlow as OwnershipFlowData,
  type OwnershipOwner,
} from '../data/api';

/**
 * Who owned the animals, in order, and how many each holds.
 *
 * Built in the same register as the live animal tracker (`ValueChainBanner`),
 * which is this app's best-looking surface: one big tracking-tight numeral per
 * node, a small-caps label under it, and a dashed rule running behind the row
 * that each numeral's own background punches through. Re-using that language
 * rather than inventing a second one is the whole point -- a chain of parties
 * should read the way a chain of places already does.
 *
 * Two rows, not one. The top row is the chain: who, in what order, with the
 * date title passed on the arrow between them. The bottom row is the count each
 * party holds, given the display weight because it is the figure people came
 * for. A middle party is usually on zero, and when it is not, those are animals
 * that came in and never went out -- the ones that died on its watch. AVCS
 * holds 2 of A260002's 139 for exactly that reason.
 *
 * Read-only. Handovers derive from the purchase and from each dispatch to an
 * abattoir, so there is nothing to type here.
 */

const ENTITY_TYPE_LABELS: Record<string, string> = {
  FARM: 'Farm',
  FEEDLOT: 'Feedlot',
  ABATTOIR: 'Abattoir',
  TRANSPORT: 'Transport',
  WOOL_PROCESSOR: 'Wool processor',
  AGENT: 'Agent / trader',
  OTHER: 'Other',
};

const fmtDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const fmtNum = (value?: number | null) =>
  value === null || value === undefined ? '—' : new Intl.NumberFormat('en-ZA').format(value);

function partyLabel(entityType: string, entityTypeOther: string): string {
  if (entityType === 'OTHER' && entityTypeOther) return entityTypeOther;
  return ENTITY_TYPE_LABELS[entityType] || entityType || 'Party';
}

/** Top row: the ordinal, the party, what it is. */
function OwnerNode({ owner, index, isCurrent }: { owner: OwnershipOwner; index: number; isCurrent: boolean }) {
  return (
    <div className="flex min-w-0 flex-col items-center">
      {/* Opaque background: the marker sits ON the dashed rule and has to cut
          it, the way the tracker's numerals do. */}
      <span
        className={`grid h-14 w-14 shrink-0 place-items-center rounded-full border-2 bg-white text-lg font-semibold tabular-nums transition-colors ${
          isCurrent ? 'border-[#1e6bb8] text-[#1e6bb8]' : 'border-[#1e6bb840] text-avcs-muted'
        }`}
        aria-hidden
      >
        {index + 1}
      </span>
      {/* Fixed two-line block: a long name like "SP Erasmus - Beskuitfontein
          Boerdery" would otherwise push its own type label a line below its
          neighbours', and the row stops reading as a row. */}
      <span
        className="mt-5 line-clamp-2 min-h-[3.25rem] min-w-0 text-balance text-lg font-semibold leading-snug tracking-tight text-avcs-text"
        title={owner.registeredBusinessName || owner.name}
      >
        {owner.name}
      </span>
      <span className="mt-1 text-[11px] font-medium uppercase tracking-[0.09em] text-avcs-muted">
        {partyLabel(owner.entityType, owner.entityTypeOther)}
      </span>
    </div>
  );
}

/** Between two nodes: when title passed. */
function HandoverLeg({ date }: { date: string | null }) {
  return (
    <div className="flex min-w-0 flex-col items-center">
      <span className="grid h-14 place-items-center text-3xl leading-none text-[#1e6bb866]" aria-hidden>→</span>
      {/* Same two-line reserve as an owner's name, so "sold" sits level with
          the party types either side of it. */}
      <span className="mt-5 min-h-[3.25rem] text-[11px] font-medium uppercase tracking-[0.09em] text-avcs-muted">
        sold
      </span>
      <span className="text-base font-semibold tabular-nums text-avcs-text">{fmtDate(date)}</span>
    </div>
  );
}

/** Bottom row: the count, at display size. */
function HoldingCell({ owner }: { owner: OwnershipOwner }) {
  const held = owner.currentlyOwned;
  return (
    <div className="flex min-w-0 flex-col items-center">
      <span
        className={`text-7xl font-bold leading-none tracking-tight tabular-nums ${
          held ? 'text-avcs-text' : 'text-[#1e6bb833]'
        }`}
      >
        {fmtNum(held)}
      </span>
      <span className="mt-4 text-xs font-medium uppercase tracking-[0.09em] text-avcs-muted">
        {held === 1 ? 'animal held' : 'animals held'}
      </span>
      {/* Once the abattoir owns them, what came off the line is the number that
          matters -- so it earns a line of its own, under the head. */}
      {owner.carcassCount !== null && (
        <span className="mt-3 text-base font-semibold tabular-nums text-avcs-text">
          {fmtNum(owner.carcassCount)}
          <span className="ml-1.5 text-[11px] font-medium uppercase tracking-[0.09em] text-avcs-muted">
            {owner.carcassCount === 1 ? 'carcass' : 'carcasses'}
          </span>
        </span>
      )}
    </div>
  );
}

export type OwnershipFlowProps = {
  dealId: string;
  /** Bumped by the parent when the deal's money or events change. */
  refreshToken?: number;
};

export default function OwnershipFlow({ dealId, refreshToken = 0 }: OwnershipFlowProps) {
  const [flow, setFlow] = useState<OwnershipFlowData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!dealId) return;
    setLoading(true);
    setError('');
    fetchOwnershipFlow(dealId)
      .then(setFlow)
      .catch((e: any) => setError(e?.message || 'Could not load the ownership flow.'))
      .finally(() => setLoading(false));
  }, [dealId]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const owners = flow?.owners ?? [];
  const hops = flow?.hops ?? [];
  // A column per owner and a narrower one per arrow, so both rows sit on the
  // same grid and every count lands under its own party.
  const columns = owners
    .flatMap((_owner, index) => (index === 0 ? ['minmax(0,1fr)'] : ['minmax(0,0.7fr)', 'minmax(0,1fr)']))
    .join(' ');
  const grid = { gridTemplateColumns: columns || 'minmax(0,1fr)' };
  // Half of an owner column, as a percentage of the whole track: the rule runs
  // between the first and last markers rather than to the container's edges.
  const trackWidth = owners.length + 0.7 * Math.max(owners.length - 1, 0);
  const ruleInset = trackWidth > 0 ? `${(0.5 / trackWidth) * 100}%` : '0%';

  return (
    <section className="py-2" aria-label="Ownership flow">
      {error && (
        <div className="mx-auto mb-6 max-w-2xl rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">
          {error}
        </div>
      )}

      {loading && owners.length === 0 && (
        <p className="py-10 text-center text-sm text-avcs-muted">Loading…</p>
      )}

      {!loading && owners.length === 0 && !error && (
        <p className="mx-auto max-w-md py-10 text-center text-sm text-avcs-muted">
          No handover recorded yet. The chain builds itself from the purchase and from each dispatch to an
          abattoir, so it fills in as those are captured.
        </p>
      )}

      {owners.length > 0 && (
        /* Capped and centred: on a wide screen an uncapped strip drifts to the
           edges and stops reading as one chain. */
        <div className="mx-auto max-w-5xl px-2 py-8">
          <div className="relative">
            <div
              className="pointer-events-none absolute top-[28px] hidden border-t border-dashed border-[#1e6bb840] sm:block"
              style={{ left: ruleInset, right: ruleInset }}
              aria-hidden
            />
            <ol className="relative z-10 grid items-start gap-x-6 gap-y-8" style={grid}>
              {owners.map((owner, index) => (
                <React.Fragment key={`${owner.id}-${index}`}>
                  {index > 0 && <li className="list-none"><HandoverLeg date={hops[index - 1]?.firstDate ?? null} /></li>}
                  <li className="list-none text-center">
                    <OwnerNode owner={owner} index={index} isCurrent={index === owners.length - 1} />
                  </li>
                </React.Fragment>
              ))}
            </ol>
          </div>

          <div className="mt-12 grid items-start gap-x-6 border-t border-[#1e6bb814] pt-10" style={grid}>
            {owners.map((owner, index) => (
              <React.Fragment key={`held-${owner.id}-${index}`}>
                {index > 0 && <div aria-hidden />}
                <HoldingCell owner={owner} />
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {(flow?.gaps ?? []).length > 0 && (
        <ul className="mx-auto mt-4 grid max-w-2xl gap-1">
          {flow!.gaps.map((gap) => (
            <li
              key={gap}
              className="rounded-lg border border-dashed border-[#e0c48a] bg-[#fdf6e7] px-3 py-1.5 text-center text-xs text-[#5c4300]"
            >
              {gap}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
